import { Image as ImageIcon, Eye, Download } from "lucide-react";
import type { CreativeItem } from "../types/creatives.types";

interface CreativeCardProps {
  item: CreativeItem;
  index: number;
  onClick?: () => void;
  onView?: (index: number) => void;
}

export function CreativeCard({
  item,
  index,
  onClick,
  onView,
}: CreativeCardProps) {
  const { image_url, format, product_image_url } = item;
  const name = item.name || item.product_name || "Sin nombre";

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!image_url) return;

    try {
      const response = await fetch(image_url);
      const blob = await response.blob();
      
      const urlPath = new URL(image_url).pathname;
      const urlExtension = urlPath.match(/\.([^.]+)$/)?.[1]?.toLowerCase();
      
      const mimeExtension = blob.type.split("/")[1]?.split(";")[0];
      const extension = urlExtension || mimeExtension || "bin";
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name.replace(/\s+/g, "-")}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    onView?.(index);
  };

  return (
    <div className="relative overflow-hidden rounded-xl bg-white border border-gray-200/50 transition-all duration-200 hover:shadow-md hover:border-gray-300 group">
      <button
        type="button"
        onClick={onClick ?? (() => console.log("click creative card", index))}
        className="relative w-full aspect-square overflow-hidden bg-gray-100/20"
      >
        <div className="relative overflow-hidden h-full w-full">
          {image_url ? (
            <img
              src={image_url}
              alt={name}
              loading="lazy"
              decoding="async"
              className="opacity-100 h-full w-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
              <ImageIcon className="h-8 w-8" />
            </div>
          )}
        </div>
        {/* Format badge in top right corner */}
        <div className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-md bg-gray-900/50 backdrop-blur-sm text-[10px] font-medium text-white">
          {format}
        </div>
        {/* Hover overlay with action buttons */}
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 group-hover:bg-black/40 transition-colors duration-200">
          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            <button
              onClick={handleView}
              className="h-8 w-8 rounded-full bg-white/90 flex items-center justify-center text-gray-900 hover:bg-white shadow-sm transition-colors"
              title="Ver"
              aria-label="Ver creativo"
            >
              <Eye className="h-4 w-4" />
            </button>
            <button
              onClick={handleDownload}
              className="h-8 w-8 rounded-full bg-white/90 flex items-center justify-center text-gray-900 hover:bg-white shadow-sm transition-colors"
              title="Descargar"
              aria-label="Descargar creativo"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>
      </button>
      
      {/* Footer with product name */}
      <div className="px-3 py-2.5 flex items-center gap-2">
        <div className="h-5 w-5 rounded-full overflow-hidden bg-gray-100/50 border border-gray-200/30 flex-shrink-0">
          {product_image_url ? (
            <img
              src={product_image_url}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <ImageIcon className="h-3 w-3 text-gray-400" />
            </div>
          )}
        </div>
        <span className="text-sm font-medium text-gray-900 truncate leading-tight">
          {name}
        </span>
      </div>
    </div>
  );
}

